import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Download, CheckCircle2, Calendar, FileText, Shield, TrendingUp } from "lucide-react";

const leadFormSchema = z.object({
  full_name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  institution: z.string().trim().max(200, "Institution name too long").optional(),
  job_title: z.string().trim().max(100, "Job title too long").optional(),
  phone: z.string().trim().max(20, "Phone number too long").optional(),
});

type LeadFormData = z.infer<typeof leadFormSchema>;

const ComplianceChecklist = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      full_name: "",
      email: "",
      institution: "",
      job_title: "",
      phone: "",
    },
  });

  const handlePDFDownload = () => {
    // Trigger PDF download
    const link = document.createElement('a');
    link.href = '/assets/post-house-compliance-checklist.pdf';
    link.download = 'Post-House-Compliance-Checklist.pdf';
    link.click();
  };

  const onSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("leads")
        .insert({
          email: data.email,
          full_name: data.full_name,
          institution: data.institution || null,
          job_title: data.job_title || null,
          phone: data.phone || null,
          lead_source: "compliance_checklist",
          pdf_downloaded: true,
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Email already registered",
            description: "This email has already downloaded the checklist. We'll send it to you again.",
            variant: "default",
          });
          handlePDFDownload();
          setIsSubmitted(true);
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Success!",
          description: "Your download is starting now. Check your downloads folder.",
        });
        handlePDFDownload();
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error("Error submitting lead form:", error);
      toast({
        title: "Error",
        description: "Failed to submit form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="text-sm font-light tracking-wide text-foreground hover:text-primary transition-colors"
          >
            AthleteNexus
          </button>
          <Button variant="ghost" onClick={() => navigate("/auth")}>
            Sign In
          </Button>
        </div>
      </nav>

      <div className="pt-32 pb-16 px-8">
        <div className="max-w-[900px] mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-light leading-tight tracking-tight text-foreground mb-6">
              Navigate Post-House v. NCAA Compliance With Confidence
            </h1>
            <p className="text-xl font-light text-muted-foreground leading-relaxed max-w-[700px] mx-auto mb-8">
              Download your free comprehensive checklist for managing revenue sharing, athlete agreements, and compliance obligations under the new settlement.
            </p>
          </div>

          {/* Problem Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="border-border">
              <CardContent className="pt-6">
                <Shield className="w-10 h-10 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Complex Requirements</h3>
                <p className="text-sm text-muted-foreground">
                  New revenue sharing rules require immediate policy updates and athlete disclosure protocols
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <TrendingUp className="w-10 h-10 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Title IX Implications</h3>
                <p className="text-sm text-muted-foreground">
                  Gender equity monitoring becomes critical with revenue sharing distributions
                </p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-6">
                <FileText className="w-10 h-10 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Tight Deadlines</h3>
                <p className="text-sm text-muted-foreground">
                  Athletic departments must implement new workflows within 90 days of settlement
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Form Card */}
          {!isSubmitted ? (
            <Card className="border-border shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Download Your Free Compliance Checklist</CardTitle>
                <CardDescription>
                  Get instant access to our Post-House Settlement Compliance Checklist with actionable items for weeks 1, 30, and 90.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="full_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="John Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="john.doe@university.edu" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="institution"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Institution (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="University Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="job_title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Job Title (Optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Compliance Officer, Athletic Director, etc." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number (Optional)</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base" 
                      disabled={isSubmitting}
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {isSubmitting ? "Processing..." : "Download Checklist Now"}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      By downloading, you agree to receive occasional updates about NIL compliance best practices. Unsubscribe anytime.
                    </p>
                  </form>
                </Form>

                {/* Checklist Preview */}
                <div className="mt-8 pt-8 border-t border-border">
                  <h3 className="font-semibold mb-4">What's Inside the Checklist:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Week 1 Action Items:</strong> Immediate revenue sharing disclosure requirements and athlete agreement templates
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        <strong className="text-foreground">30-Day Implementation:</strong> Technology stack evaluation, staff training, and compliance workflow setup
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        <strong className="text-foreground">90-Day Roadmap:</strong> Title IX monitoring framework, brand partnership protocols, and collective coordination
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Ongoing Monitoring:</strong> Weekly tasks, monthly reporting, and quarterly audit procedures
                      </span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Alert className="border-primary/50 bg-primary/5">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <AlertTitle className="text-xl mb-2">Success! Your Download is Ready</AlertTitle>
              <AlertDescription className="space-y-4">
                <p className="text-base">
                  The Post-House Compliance Checklist has been downloaded. Check your downloads folder for the PDF.
                </p>
                <div className="pt-4">
                  <h4 className="font-semibold mb-3">Ready to See AthleteNexus in Action?</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Schedule a personalized demo to see how our platform can streamline your compliance workflows and save your team hundreds of hours.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      size="lg"
                      onClick={() => window.open("https://cal.com/jay-gauthier-the-mad-jotter-lhon2h/30min?overlayCalendar=true", "_blank")}
                      className="flex items-center gap-2"
                    >
                      <Calendar className="w-5 h-5" />
                      Schedule Your Demo
                    </Button>
                    <Button 
                      size="lg"
                      variant="outline"
                      onClick={() => navigate("/")}
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Social Proof */}
          {!isSubmitted && (
            <div className="text-center mt-8">
              <p className="text-sm text-muted-foreground">
                Trusted by compliance officers at <strong>50+ athletic departments</strong> nationwide
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-8 mt-16">
        <div className="max-w-[900px] mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © 2025 AthleteNexus. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ComplianceChecklist;
